import React from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { useAuth } from '@/context/AuthContext';
import { ChevronRight, CheckCircle2 } from 'lucide-react';

function NavSection(props) {
  var title = props.title;
  var pages = props.pages;
  var currentPage = props.currentPage;
  var setCurrentPage = props.setCurrentPage;
  var completedPages = props.completedPages;
  
  return (
    <div className="mb-4">
      <div className="nav-section-header flex items-center gap-2">
        <span>{title}</span>
      </div>
      <div className="space-y-0.5">
        {pages.map(function(page) {
          var isActive = currentPage === page.id;
          var completed = completedPages.indexOf(page.id) !== -1;
          return (
            <button
              key={page.id}
              onClick={function() { setCurrentPage(page.id); }}
              className={'nav-item w-full text-left ' + (isActive ? 'active' : '')}
            >
              <div className="flex items-center gap-2 flex-1">
                {completed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                ) : (
                  <ChevronRight className={'h-3.5 w-3.5 shrink-0 ' + (isActive ? 'rotate-90' : '')} />
                )}
                <span className="truncate text-sm">{page.title}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Sidebar() {
  var workbook = useWorkbook();
  var auth = useAuth();
  var isFacilitator = auth.user && auth.user.facilitator_access === true;
  var currentPage = workbook.currentPage;
  var setCurrentPage = workbook.setCurrentPage;
  var sidebarOpen = workbook.sidebarOpen;
  var formData = workbook.formData;
  var completedPages = formData.completedPages || [];

  var frontMatter = [
    { id: 'title', title: 'Title Page' },
    { id: 'copyright', title: 'Copyright' },
    { id: 'how-to-use', title: 'How to Use' },
    { id: 'toc', title: 'Contents' }
  ];
  
  var section1 = [
    { id: 'section-1-divider', title: 'Intro' },
    { id: 'rule-1', title: 'Rule 1: Know Your Numbers' },
    { id: 'rule-2', title: 'Rule 2: Multiple Accounts' },
    { id: 'rule-3', title: 'Rule 3: 10th & 25th System' },
    { id: 'rule-4', title: 'Rule 4: Cut Without Shame' }
  ];
  
  var section2 = [
    { id: 'section-2-divider', title: 'Intro' },
    { id: 'rule-5', title: 'Rule 5: Build Your Buffer' },
    { id: 'rule-6', title: 'Rule 6: Plan for Irregular' },
    { id: 'rule-7', title: 'Rule 7: Spread Lump Sums' },
    { id: 'rule-8', title: 'Rule 8: Debt Snowball' }
  ];
  
  var section3 = [
    { id: 'section-3-divider', title: 'Intro' },
    { id: 'rule-9', title: 'Rule 9: Understand Credit' },
    { id: 'rule-10', title: 'Rule 10: Housing Decisions' },
    { id: 'rule-11', title: 'Rule 11: Smart Borrowing' }
  ];
  
  var section4 = [
    { id: 'section-4-divider', title: 'Intro' },
    { id: 'rule-12', title: 'Rule 12: Heal Money Story' },
    { id: 'rule-13', title: 'Rule 13: Find Your Purpose' },
    { id: 'rule-14', title: 'Rule 14: Entrepreneurship' }
  ];
  
  var section5 = [
    { id: 'section-5-divider', title: 'Intro' },
    { id: 'life-money-map', title: 'Life Map' },
    { id: 'commitment', title: 'Commitment' }
  ];
  
  var section6 = [
    { id: 'section-6-divider', title: 'Intro' },
    { id: 'glossary', title: 'Glossary' },
    { id: 'weekly-calculator', title: 'Calculator' },
    { id: 'check-in-sheets', title: 'Check-In' },
    { id: 'debt-tracker', title: 'Debt' },
    { id: 'credit-checklist', title: 'Credit' },
    { id: 'emergency-tracker', title: 'Emergency' },
    { id: 'irregular-planner', title: 'Irregular' },
    { id: 'crisis-mode', title: 'Crisis' },
    { id: 'reset-page', title: 'Reset' }
  ];
  
  var facilitatorGuide = [
    { id: 'program-description', title: 'Program Description' },
    { id: 'facilitator-note', title: 'Facilitator Note' }
  ];

  return (
    <aside className={'fixed left-0 top-0 h-full z-40 bg-card border-r border-border transition-transform duration-300 w-72 ' + (sidebarOpen ? 'translate-x-0' : '-translate-x-full')}>
      <div className="p-5 border-b border-border">
        <h1 className="font-serif text-lg font-bold text-foreground">The Rules of Money</h1>
        <p className="text-xs text-muted-foreground mt-1">Workbook</p>
      </div>
      
      <div className="h-[calc(100vh-80px)] overflow-y-auto p-3">
        <NavSection title="Front Matter" pages={frontMatter} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="I: Stability" pages={section1} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="II: Control" pages={section2} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="III: Growth" pages={section3} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="IV: Healing" pages={section4} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="V: Capstone" pages={section5} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        <NavSection title="VI: Tools" pages={section6} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        {isFacilitator && (
          <NavSection title="Facilitator Guide" pages={facilitatorGuide} currentPage={currentPage} setCurrentPage={setCurrentPage} completedPages={completedPages} />
        )}
      </div>
    </aside>
  );
}

export { Sidebar };
